import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useCart } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ShoppingBag, Minus, Plus, Trash2, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const CartPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { cart, updateQuantity, removeFromCart, clearCart, cartTotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [formData, setFormData] = useState({
    shipping_address: "",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error("Il carrello è vuoto");
      return;
    }

    if (!formData.shipping_address) {
      toast.error("Inserisci l'indirizzo di spedizione");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders`, {
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.discount_price || item.price,
          quantity: item.quantity
        })),
        total: cartTotal,
        shipping_address: formData.shipping_address,
        notes: formData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOrderId(response.data.id);
      setSuccess(true);
      clearCart();
      toast.success("Ordine inviato con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'invio dell'ordine");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="cart-success">
        <div className="text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
            <CheckCircle className="text-emerald-500" size={48} />
          </div>
          <h1 className="text-2xl font-light text-white">Ordine Confermato!</h1>
          <p className="text-white/60">
            Ordine #{orderId.slice(0, 8)} ricevuto. Ti contatteremo per confermare la consegna.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/profile")}
              className="bg-emerald-500 text-black hover:bg-emerald-400"
            >
              Vedi Ordini
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/shop")}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Continua Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="cart-page">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="text-white hover:text-white/70"
          data-testid="back-button"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-light text-white tracking-wide">Carrello</h1>
      </div>

      {cart.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-white/60">
          <ShoppingBag size={64} className="mb-4 opacity-30" />
          <p className="text-lg mb-2">Il carrello è vuoto</p>
          <Button
            onClick={() => navigate("/shop")}
            className="mt-4 bg-emerald-500 text-black"
          >
            Vai allo Shop
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="p-4 space-y-6">
          {/* Cart Items */}
          <div className="space-y-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className="flex gap-4 bg-zinc-900 p-3 rounded-lg border border-white/5"
                data-testid={`cart-item-${item.id}`}
              >
                {/* Image */}
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                  <img
                    src={item.images?.[0] || "https://images.unsplash.com/photo-1760072513403-d70003481414?w=200"}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-white truncate">{item.name}</h3>
                  <p className="text-emerald-500 font-bold mt-1">
                    €{(item.discount_price || item.price).toFixed(2)}
                  </p>

                  {/* Quantity Controls */}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center bg-zinc-800 rounded-full">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center text-white hover:text-emerald-500"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-6 text-center text-white text-sm">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center text-white hover:text-emerald-500"
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeFromCart(item.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Shipping Address */}
          <div className="space-y-2">
            <Label className="text-white/70">Indirizzo di Spedizione *</Label>
            <Textarea
              value={formData.shipping_address}
              onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
              placeholder="Via, Numero civico, CAP, Città, Provincia"
              className="bg-zinc-900 border-zinc-800 text-white min-h-[80px] focus:border-emerald-500"
              required
              data-testid="shipping-address"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-white/70">Note (opzionale)</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Eventuali note per la consegna"
              className="h-12 bg-zinc-900 border-zinc-800 text-white focus:border-emerald-500"
              data-testid="order-notes"
            />
          </div>

          {/* Summary */}
          <div className="bg-zinc-900 rounded-lg p-4 space-y-3 border border-white/5">
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Subtotale</span>
              <span className="text-white">€{cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Spedizione</span>
              <span className="text-white">Da calcolare</span>
            </div>
            <div className="border-t border-white/10 pt-3 flex justify-between">
              <span className="font-medium text-white">Totale</span>
              <span className="text-xl font-bold text-emerald-500">€{cartTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-full text-lg"
            data-testid="checkout-btn"
          >
            {loading ? "Invio in corso..." : "Invia Richiesta Ordine"}
          </Button>

          <p className="text-xs text-white/40 text-center">
            L'ordine verrà confermato dal nostro team che ti contatterà per il pagamento e la consegna.
          </p>
        </form>
      )}
    </div>
  );
};

export default CartPage;
